'use client'

import Link from 'next/link'
import { ArrowLeft } from 'lucide-react'
import { ReactNode } from 'react'
import { cn } from '@/utilities/cn'
import { useScrollDirection } from '@/hooks/useScrollDirection'

/**
 * Reusable page header component with back button, title, and optional right content
 *
 * @example
 * // Simple header with title and subtitle
 * <PageHeader
 *   backUrl="/dashboard"
 *   title="Create New Project"
 *   subtitle="Set up your project details"
 * />
 *
 * @example
 * // Header with right content (buttons, etc.)
 * <PageHeader
 *   backUrl="/projects"
 *   title="Project Settings"
 *   rightContent={
 *     <>
 *       <Button variant="secondary">Save</Button>
 *       <Button variant="primary">Publish</Button>
 *     </>
 *   }
 * />
 *
 * @example
 * // Sticky header that adjusts position when main navbar hides on scroll
 * <PageHeader
 *   backUrl="/scripts"
 *   title="Edit Script"
 *   sticky
 *   stickyBelowNavbar
 * />
 */

interface PageHeaderProps {
  /** The URL to navigate to when the back button is clicked */
  backUrl: string
  /** The main title of the page */
  title: string | ReactNode
  /** Optional subtitle or description */
  subtitle?: string | ReactNode
  /** Additional content to display on the right side of the header */
  rightContent?: ReactNode
  /** Whether the header should be sticky (default: false) */
  sticky?: boolean
  /** When true and sticky, positions below navbar (top-16) and moves to top-0 when navbar hides on scroll */
  stickyBelowNavbar?: boolean
  /** Additional CSS classes */
  className?: string
}

export default function PageHeader({
  backUrl,
  title,
  subtitle,
  rightContent,
  sticky = false,
  stickyBelowNavbar = false,
  className = '',
}: PageHeaderProps) {
  const isNavbarVisible = useScrollDirection({ threshold: 10 })

  // Determine top position: when stickyBelowNavbar is true, use top-16 when navbar visible, top-0 when hidden
  const topPosition = sticky && stickyBelowNavbar
    ? (isNavbarVisible ? 'top-16' : 'top-0')
    : 'top-0'

  return (
    <header className={cn(
      'bg-white border-b border-slate-200',
      sticky && 'sticky z-30 transition-[top] duration-300',
      sticky && topPosition,
      className
    )}>
      <div className="container">
        <div className="flex items-center justify-between py-4">
          {/* Left side - Back button and title */}
          <div className="flex items-center gap-4">
            <Link
              href={backUrl}
              className="p-2 hover:bg-slate-100 rounded-full transition-colors"
              aria-label="Ga terug"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>

            <div>
              <h1 className="text-xl font-semibold text-slate-900">
                {title}
              </h1>
              {subtitle && (
                <div className="text-sm text-slate-600">
                  {subtitle}
                </div>
              )}
            </div>
          </div>

          {/* Right side - Additional content */}
          {rightContent && (
            <div className="flex items-center gap-2">
              {rightContent}
            </div>
          )}
        </div>
      </div>
    </header>
  )
}
